import Component from '@bythepixel/component-loader';
import Glide from '@glidejs/glide';
import '@glidejs/glide/dist/css/glide.core.min.css';

/**
 * Features / Highlights carousel (Glide.js). Desktop shows ~2 cards with header
 * arrows; below lg it's one card + a peek, swipe-advanced. `bound: true` stops
 * looping and flags the arrows at the ends. peek.before is 0 so the first card
 * sits at the page margin; the right peek bleeds off-screen (see the track CSS).
 */
const GLIDE_OPTIONS = {
  type: 'slider',
  rewind: false,
  bound: true,
  perView: 2,
  gap: 24,
  peek: { before: 0, after: 260 },
  breakpoints: {
    1023: {
      perView: 1,
      gap: 16,
      peek: { before: 0, after: 64 },
    },
  },
};

const ARROW_SELECTOR = '[data-glide-dir]';
const DISABLED_CLASS = 'glide__arrow--disabled';

/**
 * Reflect an arrow's bound state. Glide only toggles the class (pointer-events);
 * we also set `disabled` so keyboard/AT users can't activate a disabled arrow.
 */
function setArrowDisabled(arrow, disabled) {
  arrow.classList.toggle(DISABLED_CLASS, disabled);
  arrow.setAttribute('aria-disabled', disabled ? 'true' : 'false');

  if (disabled) {
    arrow.setAttribute('disabled', '');
  } else {
    arrow.removeAttribute('disabled');
  }
}

export default class FeaturesHighlights extends Component {
  static selector = '[comp-features-highlights]';

  #glide = null;

  constructor(...args) {
    super(...args);
    this.init();
  }

  init() {
    const arrows = this.$container.querySelectorAll(ARROW_SELECTOR);
    const slides = this.$container.querySelectorAll('.glide__slide');

    // A single card has nothing to slide — stay static and disable both arrows.
    if (slides.length <= 1) {
      arrows.forEach((arrow) => setArrowDisabled(arrow, true));
      return;
    }

    this.#glide = new Glide(this.$container, GLIDE_OPTIONS);
    this.#glide.on('run.after', () => this.syncArrows());
    this.#glide.mount();
    this.syncArrows();
  }

  syncArrows() {
    this.$container.querySelectorAll(ARROW_SELECTOR).forEach((arrow) => {
      setArrowDisabled(arrow, arrow.classList.contains(DISABLED_CLASS));
    });
  }

  destroy() {
    if (this.#glide) {
      this.#glide.destroy();
      this.#glide = null;
    }
  }
}
